import React, {useState} from 'react'
import {Box, Button, CircularProgress, Grid, Stack, Tooltip, Typography} from "@mui/material"
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined"
import {useProjectData} from "../../../hooks/useProjectData"
import CompletionCard from "../../completions/CompletionCard"
import InferencePromptForm from "../../inference/InferencePromptForm"
import SynthesizeButton from "./SynthesizeButton"
import SynthesisResultJson from "../../../api/json/SynthesisResultJson"
import {CompletionRequestJson} from "../../../api/json/CompletionRequestJson"
import {CompletionOptionsJson} from "../../../api/json/CompletionOptionsJson"
import CompletionOptionsForm from "../../completions/CompletionOptionsForm"
import SpaceBetween from "../../common/SpaceBetween"
import PlaygroundCompletionRequestCard from "./PlaygroundCompletionRequestCard"
import {AnyCustomModelJson} from "../../../api/json/CustomModelJson"
import {CustomModelRequests} from "../../../api/requests/CustomModelRequests"
import AsyncButton from "../../button/AsyncButton"
import ApiError from "../../../api/ApiError"
import ErrorMessage from "../../error/ErrorMessage"

interface PlaygroundProps {
    model: AnyCustomModelJson
}

function Playground({model}: PlaygroundProps) {
    const {project} = useProjectData()

    const [promptInputs, setPromptInputs] = useState<Record<string, string>>({})
    const [options, setOptions] = useState<CompletionOptionsJson>()
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState<ApiError>()
    const [completionRequests, setCompletionRequests] = useState<CompletionRequestJson[]>([])
    const [synthesisResult, setSynthesisResult] = useState<SynthesisResultJson>()

    if (!project) {
        return <CircularProgress/>
    }

    const atLeastOneInput = Object.values(promptInputs).some(v => v.length > 0)
    const disableSynthesis = !!synthesisResult || completionRequests.length > 0

    const getCompletion = () => {
        if (!options) return
        setError(undefined)
        setGenerating(true)
        CustomModelRequests.complete(model.id, promptInputs, options)
            .then(req => setCompletionRequests(prev => [req, ...prev]))
            .catch(setError)
            .finally(() => setGenerating(false))
    }

    return (
        <Grid container spacing={4}>
            <Grid item md={5} sm={12}>
                <Stack spacing={2}>
                    <SpaceBetween sx={{minHeight: 28}}>
                        <Typography variant="subtitle2">Inputs</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={disableSynthesis ? "Clear output to enable synthesis." : ""}>
                                <Box>
                                    <SynthesizeButton project={project}
                                                      template={model.template}
                                                      disabled={disableSynthesis}
                                                      handleResult={result => {
                                                          setPromptInputs(result.referenceValues)
                                                          setSynthesisResult(result)
                                                      }}
                                    />
                                </Box>
                            </Tooltip>
                            {Object.keys(promptInputs).length > 0 &&
                                <Button size="small" onClick={() => setPromptInputs({})}>Clear</Button>
                            }
                        </Stack>
                    </SpaceBetween>

                    <InferencePromptForm columns={project.columns}
                                         template={model.template}
                                         cells={promptInputs}
                                         updateCells={setPromptInputs}
                    />

                    <Typography variant="subtitle2" sx={{mt: 2}}>Settings</Typography>
                    <CompletionOptionsForm platform={model.platform} onChange={setOptions}/>

                    <Box>
                        <AsyncButton
                            variant="contained"
                            waiting={generating}
                            tooltip={!atLeastOneInput ? "Inputs must not be empty. Type something or press Synthesize to create one automatically." : ""}
                            disabled={!atLeastOneInput || generating}
                            onClick={getCompletion}
                            endIcon={<BoltOutlinedIcon/>}
                        >
                            Generate
                        </AsyncButton>
                    </Box>
                    {error && <ErrorMessage error={error}/>}
                </Stack>
            </Grid>

            <Grid item md={7} sm={12} xs={12}>
                <SpaceBetween sx={{minHeight: 28}}>
                    <Typography variant="subtitle2">Output</Typography>
                    {(completionRequests.length > 0 || synthesisResult) &&
                        <Button variant="text" size="small"
                                onClick={() => {
                                    setCompletionRequests([])
                                    setSynthesisResult(undefined)
                                }}>
                            Clear
                        </Button>
                    }
                </SpaceBetween>

                <Stack spacing={4} sx={{mt: 2}}>
                    {synthesisResult &&
                        <Box>
                            <Typography variant="caption">Synthesis</Typography>
                            <CompletionCard
                                key={synthesisResult.id}
                                template={model.template}
                                generativeResult={synthesisResult}
                                referenceValues={synthesisResult.referenceValues}
                                sx={{mt: 1}}
                            />
                        </Box>
                    }

                    {completionRequests.map(request => (
                        <PlaygroundCompletionRequestCard key={request.id} request={request}/>
                    ))}
                </Stack>
            </Grid>
        </Grid>
    )
}

export default Playground
